/**
 * The composer's submit POLICY — what happens to the text the user typed.
 *
 * Extracted from `Composer.tsx` because this is where a message is most easily
 * lost, and it was: the input was cleared BEFORE the store was asked to take the
 * turn, so a store that could not take it (no conversation resolved yet — an
 * offline reopen, or a companion still opening) destroyed what the user wrote.
 * Nothing sent, nothing queued, nothing on screen to say so.
 *
 * Pure and fully injected (DIP) — no DOM, no store — so the rule is unit-tested
 * on its own.
 */

/** The input this policy drives, and the store it hands the turn to. */
export interface ComposerTarget {
  /** The current input text. */
  readonly read: () => string;
  /** Replace the input text. */
  readonly write: (value: string) => void;
  /** True while a turn (or a history load) is in flight — the composer is locked. */
  readonly busy: () => boolean;
  /**
   * Hand the turn to the store. `false` means it was NOT taken (see
   * `ChatStore.send`); `true` covers streaming, queued-offline and refused-with-
   * an-error alike — in all three the store owns the text from here.
   */
  readonly send: (text: string) => Promise<boolean>;
}

/**
 * Submit whatever is typed. Blank input and a busy composer are no-ops; anything
 * else is cleared optimistically (leaving it in place for the length of a run
 * invites a double-send) and PUT BACK if the store refused to take it.
 *
 * Restoring re-checks the input first: the refusal is immediate, but a user who
 * has already started typing again must never have that clobbered.
 */
export async function submitTurn(target: ComposerTarget): Promise<void> {
  const value = target.read().trim();
  if (value === '' || target.busy()) {
    return;
  }
  target.write('');
  const accepted = await target.send(value);
  if (!accepted && target.read() === '') {
    target.write(value);
  }
}
